import { useState } from 'react';
import { motion } from 'framer-motion';

import { Box, Button, Flex, Grid, GridItem, Heading } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { ShapesBanner } from '~/components/shapes-banner';

import { EnterpriseResourceCenterTemplate } from '../model';
import { EnterpriseCenterResource } from '../types';

import { HeroSection } from './hero-section';
import { NoFilterResults } from './no-filter-results';
import { ResourceCard } from './resource-card';
import { Filters, ResourceCenterFilters } from './resource-center-filters';

const DEFAULT_NUMBER_OF_RESOURCES_DISPLAYED = 12;

export function EnterpriseResourceCenter({
  enterpriseResourceCenterTemplate,
  allResources,
}: {
  enterpriseResourceCenterTemplate: EnterpriseResourceCenterTemplate;
  allResources: EnterpriseCenterResource[];
}) {
  const [loadMoreResourcesIteration, setLoadMoreResourcesIteration] = useState(1);
  const [filteredResources, setFilteredResources] = useState(getResourcesWithoutResourceSectionResources(allResources));
  const [isFiltering, setIsFiltering] = useState(false);

  function handleFiltersChange(filters: Filters) {
    const hasIndustryOrKnowledgeAreaFilters = filters.industries.length > 0 || filters.knowledgeAreas.length > 0;
    const hasFilters = hasIndustryOrKnowledgeAreaFilters || filters.contentTypes.length > 0;
    let filteredResources: EnterpriseCenterResource[] = [];

    if (!hasFilters) {
      handleClearFilters();
      return;
    }

    if (hasIndustryOrKnowledgeAreaFilters) {
      const industryFilteredResources = allResources
        .filter((resource) => industryFilter(filters, resource))
        .filter((resource) => contentTypeFilter(filters, resource));

      const knowledgeAreaFilteredResources = allResources
        .filter((resource) => knowledgeAreaFilter(filters, resource))
        .filter((resource) => contentTypeFilter(filters, resource));

      filteredResources = [...industryFilteredResources, ...knowledgeAreaFilteredResources].reduce(
        (foundValues, nextResource) =>
          foundValues.some((resource) => resource._id === nextResource._id)
            ? foundValues
            : foundValues.concat(nextResource),
        [] as EnterpriseCenterResource[]
      );
    } else {
      filteredResources = allResources.filter((resource) => contentTypeFilter(filters, resource));
    }

    setFilteredResources(filteredResources);
    setIsFiltering(hasFilters);
  }

  function handleClearFilters() {
    setFilteredResources(getResourcesWithoutResourceSectionResources(allResources));
    setIsFiltering(false);
  }

  function handleLoadMoreClick() {
    const newIteration = loadMoreResourcesIteration + 1;

    setLoadMoreResourcesIteration(newIteration);
  }

  function getResourcesSubset(resources: EnterpriseCenterResource[]) {
    const numberOfDisplayedResources = DEFAULT_NUMBER_OF_RESOURCES_DISPLAYED * loadMoreResourcesIteration;

    return resources.slice(0, numberOfDisplayedResources);
  }

  function getResourcesWithoutResourceSectionResources(resources: EnterpriseCenterResource[]) {
    const resourceListSectionIds = enterpriseResourceCenterTemplate.resourceListSection.resources.map(
      (resource) => resource._id
    );

    return resources.filter((resource) => !resourceListSectionIds.includes(resource._id));
  }

  const displayedResources = getResourcesSubset(filteredResources);

  return (
    <Box>
      <HeroSection
        image={enterpriseResourceCenterTemplate.heroBackgroundImage}
        title={enterpriseResourceCenterTemplate.pageTitle}
      />

      <ContentContainer>
        <ResourceCenterFilters
          numberOfResults={filteredResources.length}
          onClearFilters={handleClearFilters}
          onFiltersChange={handleFiltersChange}
        />

        {!isFiltering && (
          <Box bg='white' borderColor='gray.300' borderRadius='4px' borderWidth='1px' marginTop='24px' padding='40px'>
            <Heading as='h2' size='marketing-lg'>
              {enterpriseResourceCenterTemplate.resourceListSection.title}
            </Heading>
            <Grid
              gap='32px'
              marginTop='16px'
              templateColumns={{
                base: 'repeat(1, 1fr)',
                md: 'repeat(3, 1fr)',
              }}
            >
              {enterpriseResourceCenterTemplate.resourceListSection.resources.map((resource) => (
                <GridItem key={resource._id}>
                  <ResourceCard key={resource._id} imagePriority={true} resource={resource} />
                </GridItem>
              ))}
            </Grid>
          </Box>
        )}

        <Box marginTop='32px'>
          <Grid
            gap='32px'
            templateColumns={{
              base: 'repeat(1, 1fr)',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
              xl: 'repeat(4, 1fr)',
            }}
          >
            {displayedResources.map((resource) => (
              <GridItem key={resource._id}>
                <ResourceCard resource={resource} />
              </GridItem>
            ))}
          </Grid>
        </Box>

        {!Boolean(displayedResources.length) && (
          <Box
            key='enterprise-resource-no-results'
            animate={{ opacity: 1 }}
            as={motion.div}
            exit={{ opacity: 0 }}
            flex={1}
            initial={{ opacity: 0 }}
            width='100%'
          >
            <NoFilterResults />
          </Box>
        )}
      </ContentContainer>

      {filteredResources.length > loadMoreResourcesIteration * DEFAULT_NUMBER_OF_RESOURCES_DISPLAYED && (
        <Flex justifyContent='center' marginTop='64px' width='100%'>
          <Button variant='outline' onClick={handleLoadMoreClick}>
            Load More Resources
          </Button>
        </Flex>
      )}

      <Box h={{ base: '80px', md: '120px' }} />

      <ShapesBanner flipTitleAndSubtitleOrder={true} {...enterpriseResourceCenterTemplate.shapesBanner} />
    </Box>
  );
}

function industryFilter(filters: Filters, resource: EnterpriseCenterResource) {
  if (!resource.industries || filters.industries.length === 0) {
    return false;
  }

  return filters.industries.some((industry) => resource.industries?.includes(industry));
}

function knowledgeAreaFilter(filters: Filters, resource: EnterpriseCenterResource) {
  if (!resource.knowledgeAreas || filters.knowledgeAreas.length === 0) {
    return false;
  }

  return filters.knowledgeAreas.some((knowledgeArea) => resource.knowledgeAreas?.includes(knowledgeArea));
}

function contentTypeFilter(filters: Filters, resource: EnterpriseCenterResource) {
  if (filters.contentTypes.length > 0) {
    return (
      (resource.type === 'case-study' && filters.contentTypes.includes('caseStudy')) ||
      (resource.type === 'report' && filters.contentTypes.includes('report')) ||
      (resource.type === 'webinar' && filters.contentTypes.includes('webinar')) ||
      (resource.type === 'live-event' && filters.contentTypes.includes('live-event')) ||
      (resource.type === 'ebook' && filters.contentTypes.includes('ebook')) ||
      (resource.type === 'infographic' && filters.contentTypes.includes('infographic'))
    );
  }

  return true;
}
